import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import AuthContext, { logoutUser } from '../../routes/AuthContext';
import { useFeatureToggleWhenReady } from '../../hooks/use-feature-toggle';
import { inactiveUserTimeoutTreatment } from '../../config';
import { createInactivityCrossTabSync, InactivityCrossTabSync } from './crossTabSync';

interface InactivityLogoutProps {
  countdownDuration?: number; // seconds, default: 60
}

const DEFAULT_COUNTDOWN_DURATION = 60; // 60 seconds
const DEFAULT_INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel',
];

const InactivityLogout: React.FC<InactivityLogoutProps> = ({
  countdownDuration = DEFAULT_COUNTDOWN_DURATION,
}) => {
  const keycloak = useContext(AuthContext);
  const [isFeatureEnabled] = useFeatureToggleWhenReady(inactiveUserTimeoutTreatment, 'FACILITY');
  const [showModal, setShowModal] = useState(false);
  const [countdown, setCountdown] = useState(countdownDuration);

  const inactivityTimeoutMs = useMemo(() => {
    const tokenTimeout = keycloak.tokenParsed?.['inactive-user-timeout'];
    if (typeof tokenTimeout === 'number' && tokenTimeout >= 2) {
      return tokenTimeout * 60 * 1000;
    }
    return DEFAULT_INACTIVITY_TIMEOUT_MS;
  }, [keycloak.tokenParsed]);

  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncRef = useRef<InactivityCrossTabSync | null>(null);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const clearCountdownInterval = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const startInactivityTimer = useCallback(() => {
    clearInactivityTimer();

    const now = Date.now();
    const storedLastActivity = syncRef.current?.getLastActivity() ?? null;
    // A stale timestamp from a previous session (e.g., browser closed without
    // an explicit logout) would otherwise pop the warning modal immediately on
    // next login. Treat anything older than the timeout as no recorded activity.
    const lastActivity =
      storedLastActivity != null && now - storedLastActivity < inactivityTimeoutMs
        ? storedLastActivity
        : now;
    const warningAt = lastActivity + inactivityTimeoutMs - countdownDuration * 1000;
    const timeUntilWarning = warningAt - now;

    if (timeUntilWarning <= 0) {
      setShowModal(true);
      setCountdown(countdownDuration);
      return;
    }

    inactivityTimerRef.current = setTimeout(() => {
      setShowModal(true);
      setCountdown(countdownDuration);
    }, timeUntilWarning);
  }, [countdownDuration, clearInactivityTimer, inactivityTimeoutMs]);

  const handleActivity = useCallback(() => {
    // Only reset the timer if the modal is not showing
    if (!showModal) {
      syncRef.current?.notifyActivity();
      startInactivityTimer();
    }
  }, [showModal, startInactivityTimer]);

  const handleStayLoggedIn = useCallback(() => {
    clearCountdownInterval();
    setShowModal(false);
    setCountdown(countdownDuration);
    syncRef.current?.notifyStayLoggedIn();
    startInactivityTimer();
  }, [countdownDuration, clearCountdownInterval, startInactivityTimer]);

  const handleLogout = useCallback(() => {
    clearInactivityTimer();
    clearCountdownInterval();
    syncRef.current?.notifyLogout();
    logoutUser();
  }, [clearInactivityTimer, clearCountdownInterval]);

  // Cross-tab coordination: share activity, stay-logged-in, and logout across tabs.
  // Declared before the activity-listener effect so syncRef is populated before
  // the first startInactivityTimer() call on mount.
  useEffect(() => {
    if (!isFeatureEnabled) return;

    const sync = createInactivityCrossTabSync();
    syncRef.current = sync;

    const unsubscribe = sync.subscribe((msg) => {
      switch (msg.type) {
        case 'activity':
        case 'stayLoggedIn':
          // Activity in another tab means the user is present; dismiss any
          // pending warning here and reschedule against the shared timestamp.
          clearCountdownInterval();
          setShowModal(false);
          setCountdown(countdownDuration);
          startInactivityTimer();
          break;
        case 'logout':
          clearInactivityTimer();
          clearCountdownInterval();
          logoutUser();
          break;
      }
    });

    return () => {
      unsubscribe();
      sync.close();
      syncRef.current = null;
    };
  }, [
    isFeatureEnabled,
    clearCountdownInterval,
    clearInactivityTimer,
    countdownDuration,
    startInactivityTimer,
  ]);

  // Set up activity listeners and inactivity timer
  useEffect(() => {
    if (!isFeatureEnabled) return;

    startInactivityTimer();

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearInactivityTimer();
      clearCountdownInterval();
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [
    isFeatureEnabled,
    startInactivityTimer,
    handleActivity,
    clearInactivityTimer,
    clearCountdownInterval,
  ]);

  // Handle countdown when modal is shown
  useEffect(() => {
    if (showModal) {
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else {
      clearCountdownInterval();
    }

    return () => {
      clearCountdownInterval();
    };
  }, [showModal, clearCountdownInterval]);

  // Log out immediately when countdown reaches 0
  useEffect(() => {
    if (showModal && countdown <= 0) {
      clearCountdownInterval();
      syncRef.current?.notifyLogout();
      logoutUser();
    }
  }, [showModal, countdown, clearCountdownInterval]);

  // When a backgrounded tab becomes visible, reconcile against the shared
  // lastActivity so throttled timers don't fire stale warnings.
  useEffect(() => {
    if (!isFeatureEnabled) return;
    const handler = () => {
      if (document.visibilityState === 'visible' && !showModal) {
        startInactivityTimer();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [isFeatureEnabled, showModal, startInactivityTimer]);

  // Dev-only console test helper
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    (window as unknown as Record<string, unknown>).__triggerInactivityModal = () => {
      setShowModal(true);
      setCountdown(countdownDuration);
    };
    return () => {
      delete (window as unknown as Record<string, unknown>).__triggerInactivityModal;
    };
  }, [countdownDuration]);

  if (!isFeatureEnabled && !showModal) {
    return null;
  }

  return (
    <BootstrapDialog
      open={showModal}
      aria-labelledby="inactivity-logout-dialog-title"
      PaperProps={{ style: { maxWidth: 400 } }}
    >
      <DialogTitle id="inactivity-logout-dialog-title">
        <Box
          display="flex"
          alignItems="center"
          gap={1}
        >
          <WarningAmberIcon color="warning" />
          <Typography
            variant="h6"
            component="span"
          >
            Session Timeout Warning
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body1"
          gutterBottom
        >
          You have been inactive and will be logged out for security purposes.
        </Typography>
        <Typography
          variant="h4"
          align="center"
          sx={{ my: 2, fontWeight: 'bold' }}
        >
          {countdown}
        </Typography>
        <Typography
          variant="body2"
          align="center"
          color="text.secondary"
        >
          seconds remaining
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleLogout}
        >
          Log Out
        </Button>
        <Button
          variant="contained"
          onClick={handleStayLoggedIn}
        >
          Stay Logged In
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
};

export default InactivityLogout;
